/**
 * lookup-charms.ts - the Member browse table's Status charms (Portal four-charm reduction, the
 * half Member Care can stand behind).
 *
 * Milestone charm (one, mutually exclusive): a results row -> RESULTED, dated by process_date (the
 * true result date; resulted_ts is the received-at-lab time, the standing 26fobt trap), else a
 * lab-received scan -> DELIVERED TO LAB (sample_received_ts). Members short of the lab render no
 * milestone charm.
 *
 * Do-not-solicit charm: the 26fobt do_not_solicit mark (positive-only signal - rendering it is
 * always right; its absence clears nothing). Stands in for the Portal's reply-classified "Do not
 * contact" pill, which needs the Portal's outreach-reply corpus Member Care does not ingest.
 *
 * GUARDS: kit_tracking_summary timestamps use EMPTY-STRING sentinels, never NULL, so every
 * predicate trims. Timestamps render WITHOUT new Date() (a date-only '2026-06-05' parses as UTC
 * midnight and can day-shift backward in local time - the Portal's no-day-shift rule).
 */

export interface CharmFields {
  /** The milestone the SERVER derived. The clinical fields it was derived from no longer reach the
   *  browse row at all (QA-113, 2026-09-06), which is why this file no longer computes it. */
  milestone?: '' | 'resulted' | 'delivered_to_lab' | null;
  milestone_ts?: string | null;
  do_not_solicit?: string | null;
}

const has = (s: string | null | undefined): s is string => !!s && s.trim() !== '';

export type MilestoneKind = 'resulted' | 'delivered_to_lab';

export const MILESTONE_LABEL: Record<MilestoneKind, string> = {
  resulted: 'Resulted',
  delivered_to_lab: 'Delivered to lab',
};

/** Whether the server derived a results milestone for this member (any flag counts, canceled
 *  included, which is the rule the derivation ports). */
export function hasResultRow(r: CharmFields): boolean {
  return r.milestone === 'resulted';
}

/** The ONE milestone charm, or null (the member renders the bare dash).
 *
 *  The DERIVATION MOVED SERVER-SIDE (QA-113): this used to read result_status / result_value /
 *  resulted_ts / process_date / sample_received_ts off the row, which is precisely why those five
 *  clinical fields were on the wire for every member in a browse listing. The rule is unchanged and
 *  now lives in the API's result-codes.milestoneOf; this reads its answer. */
export function milestoneCharm(r: CharmFields): { kind: MilestoneKind; ts: string | null } | null {
  if (r.milestone === 'resulted') return { kind: 'resulted', ts: has(r.milestone_ts) ? r.milestone_ts : null };
  if (r.milestone === 'delivered_to_lab') {
    return { kind: 'delivered_to_lab', ts: has(r.milestone_ts) ? r.milestone_ts : null };
  }
  return null;
}

/** True when 26fobt marks the member do-not-solicit (positive-only; '' is NOT a clearance). */
export function dnsCharm(r: CharmFields): boolean {
  return has(r.do_not_solicit);
}

/** Sort accessor for the Status column: the milestone charm's timestamp (lexical day-then-intraday;
 *  empties group together per the asc/desc toggle). */
export function charmSortValue(r: CharmFields): string {
  return milestoneCharm(r)?.ts ?? '';
}

/** Charm date, rendered WITHOUT new Date(): an ISO-leading string shows its day part; anything
 *  else (US dates, preformatted wall-clock) passes through raw. '' -> null (dateless charm). */
export function fmtCharmTs(ts: string | null | undefined): string | null {
  const s = (ts ?? '').trim();
  if (!s) return null;
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
}
